import { DEFAULT_THRESHOLDS } from '../rules/types';
import type { Thresholds } from '../rules/types';

function pctStr(f: number, digits = 1) {
  return `${(f * 100).toFixed(digits)}%`;
}

function Slider(props: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; help: string;
}) {
  return (
    <div className="slider">
      <label>
        <span title={props.help}>{props.label}</span>
        <span className="val">{pctStr(props.value)}</span>
      </label>
      <input
        type="range"
        min={props.min} max={props.max} step={props.step}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
        aria-label={props.label}
      />
    </div>
  );
}

/**
 * The live rule controls. Shared by Today and Watchlist because both screens are downstream of
 * the same thresholds — in the Mac app these live once in Settings, and having two copies that
 * could drift apart would misrepresent how the product works.
 *
 * Values are FRACTIONS everywhere and only become percentages at the point of display. Keeping
 * that conversion at the very edge is what prevents the percent-vs-fraction confusion that
 * caused a real bug in the Swift original.
 */
export default function ThresholdPanel({
  thresholds: t, onChange,
}: {
  thresholds: Thresholds;
  onChange: (next: Thresholds) => void;
}) {
  const set = <K extends keyof Thresholds>(k: K, v: Thresholds[K]) =>
    onChange({ ...t, [k]: v });

  return (
    <div className="panel">
      <h2>Your rules</h2>
      <p className="hint">
        Move a slider and watch the badges change. This is the actual decision logic from the
        Mac app — same thresholds, same order, ported line-for-line and covered by matching tests.
      </p>

      <div className="sliders">
        <Slider
          label="Near the 52-week low, within"
          help="How close to its 52-week low a stock must be to be considered at all."
          value={t.nearLowFraction} min={0.01} max={0.5} step={0.01}
          onChange={(v) => set('nearLowFraction', v)}
        />
        <Slider
          label="Minimum dividend yield"
          help="Below this, a near-low stock is only ever a Watch."
          value={t.minYieldFraction} min={0} max={0.12} step={0.005}
          onChange={(v) => set('minYieldFraction', v)}
        />
        <Slider
          label="Yield sanity ceiling"
          help="Above this, a hit is flagged rather than treated as a clean Buy — an unusually high yield is more often distress than a bargain."
          value={t.maxYieldFraction} min={0.04} max={0.25} step={0.005}
          onChange={(v) => set('maxYieldFraction', v)}
        />
      </div>

      <div className="toggles">
        <label>
          <input type="checkbox" checked={t.entryScreenEnabled}
                 onChange={(e) => set('entryScreenEnabled', e.target.checked)} />
          Entry screen rule
        </label>
        <label>
          <input type="checkbox" checked={t.trendFilterEnabled}
                 onChange={(e) => set('trendFilterEnabled', e.target.checked)} />
          Trend filter (200-day) — the only rule that can produce a Sell
        </label>
      </div>

      <button className="reset" onClick={() => onChange(DEFAULT_THRESHOLDS)}>
        Reset to defaults
      </button>
    </div>
  );
}
